import React from 'react';
import { DialogV2, Flex, TextV2, IconV2, Box, ButtonV2 } from '@withjoy/joykit';
import { CheckOutline, Warning, Bank } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { useCreditCardSetupTelemetry } from '../CreditCardSetUp.telemetry';
import { useMount } from '@shared/utils/hooks/useMount';
import type { PayoutAccountInfo } from '../CreditCardSetUp.context';
import { withWindow } from '@shared/utils/withWindow';
import { NeedAssistance } from '../NeedAssistance';
import { pxToRem } from '@withjoy/joykit/theme';
import { onboardingButtonOverrides } from './Introduction.styles';

type IntroductionProps = Readonly<{
  isOwner: boolean;
  initKYCOnboarding: () => void;
  initPayoutAccountLoading: boolean;
  isValidUSAddress: boolean;
  shouldSkipAddressValidation: boolean;
  payoutAccountInfo?: PayoutAccountInfo | null;
  handleInvalidShippingAddress?: (firstName?: string, lastName?: string) => void;
}>;

export const Introduction: React.FC<IntroductionProps> = ({
  isOwner,
  initKYCOnboarding,
  initPayoutAccountLoading,
  isValidUSAddress,
  shouldSkipAddressValidation,
  payoutAccountInfo,
  handleInvalidShippingAddress,
}) => {
  const telemetry = useCreditCardSetupTelemetry();
  const { t } = useTranslation('sharedRegistry');
  const { subtitle, onlyForOwners, onlyForUS, continueText, joyWalletTitle, joyWalletDescription, connectBankAccount, stripeDisclaimer, learnMore, addAddress } =
    t('creditCardSetUpDialog');
  useMount(() => {
    telemetry.creditCardSetupDialog.enter(payoutAccountInfo?.status || 'not-started');
  });
  return (
    <DialogV2.Body>
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <Box
          position="relative"
          marginTop={10}
          role="figure"
          __css={{
            svg: {
              width: pxToRem(64),
              height: pxToRem(64),
            },
          }}
        >
          <Bank />
        </Box>
        <TextV2
          textAlign="center"
          color="mono14"
          typographyVariant="display2"
          marginY={5}
        >
          {joyWalletTitle()}
        </TextV2>
      </Flex>
      <Flex
        flexDirection="column"
        alignItems="center"
        paddingX={{ base: 8, md: pxToRem(112) }}
      >
        <TextV2
          color="mono12"
          textAlign="center"
          typographyVariant="body3"
        >
          {joyWalletDescription()}
        </TextV2>
        <Box marginTop={8}>
          <TextV2
            color="mono14"
            typographyVariant="hed3"
          >
            {subtitle()}
          </TextV2>
          <CreditCardSetupStepInformation />
        </Box>
      </Flex>
      <Flex
        rowGap={3}
        alignItems="center"
        justifyContent="center"
        marginTop={8}
      >
        {isOwner && (shouldSkipAddressValidation || isValidUSAddress) && (
          <ButtonV2
            loading={initPayoutAccountLoading}
            shape="rounded"
            width={pxToRem(332)}
            onClick={() => {
              if (!payoutAccountInfo) {
                telemetry.letsGetsStartedButtonClicked();
                initKYCOnboarding();
              } else {
                telemetry.kycContinueButtonClicked(payoutAccountInfo);
                withWindow(window => {
                  window.open(payoutAccountInfo.onboardingUrl || payoutAccountInfo.serviceDashboardUrl, '_self');
                });
              }
            }}
            overrides={onboardingButtonOverrides}
          >
            {!payoutAccountInfo ? connectBankAccount() : continueText()}
          </ButtonV2>
        )}
      </Flex>
      <Flex
        flexDirection="column"
        alignItems="center"
        marginTop={8}
      >
        {payoutAccountInfo && isOwner && <NeedAssistance />}
        {!isOwner && (
          <Flex
            maxWidth="500px"
            rowGap={3}
            justifyContent="center"
            padding={5}
            borderRadius={3}
            borderWidth="1px"
            borderStyle="solid"
            borderColor="info3"
            marginY={3}
          >
            <IconV2
              color="info5"
              marginRight={2}
            >
              <Warning />
            </IconV2>
            <TextV2 typographyVariant="body1">{onlyForOwners()}</TextV2>
          </Flex>
        )}

        {!isValidUSAddress && !shouldSkipAddressValidation && (
          <Flex
            maxWidth="500px"
            rowGap={3}
            justifyContent="center"
            padding={5}
            borderRadius={3}
            borderWidth="1px"
            borderStyle="solid"
            borderColor="info3"
            marginY={3}
          >
            <IconV2
              color="info5"
              marginRight={2}
            >
              <Warning />
            </IconV2>
            <TextV2
              typographyVariant="body1"
              color="mono14"
            >
              {onlyForUS()}
              <ButtonV2
                color="mono14"
                fontWeight={600}
                textDecoration="underline"
                intent="neutral"
                variant="link"
                typographyVariant="body1"
                size="sm"
                onClick={() => handleInvalidShippingAddress?.()}
                style={{ textDecorationColor: '#767676' }}
              >
                {addAddress()}
              </ButtonV2>
            </TextV2>
          </Flex>
        )}
      </Flex>
      <Flex
        justifyContent="center"
        marginTop={8}
      >
        <Flex
          maxWidth="450px"
          flexDirection="column"
        >
          <TextV2
            textAlign="center"
            color="mono10"
            typographyVariant="label1"
          >
            {stripeDisclaimer()}{' '}
            <ButtonV2
              textDecoration="underline"
              color="mono10"
              variant="link"
              size="sm"
              as="a"
              href="https://stripe.com/legal/connect-account"
              typographyVariant="label1"
              target="_blank"
            >
              {learnMore()}
            </ButtonV2>
          </TextV2>
        </Flex>
      </Flex>
    </DialogV2.Body>
  );
};

const CreditCardSetupStepInformation: React.FC = () => {
  const { t } = useTranslation('sharedRegistry');
  const { recipient, bank, legalName } = t('creditCardSetUpDialog');
  return (
    <Box
      marginTop={5}
      justifyContent="center"
    >
      <SetupRequirement>{legalName()}</SetupRequirement>
      <SetupRequirement>{recipient()}</SetupRequirement>
      <SetupRequirement>{bank()}</SetupRequirement>
    </Box>
  );
};

const SetupRequirement: React.FC = ({ children }) => {
  return (
    <Flex
      as="li"
      paddingY={2}
      alignItems="center"
    >
      <IconV2
        marginRight={4}
        color="mono12"
      >
        <CheckOutline />
      </IconV2>
      <TextV2
        color="mono12"
        typographyVariant="body3"
      >
        {children}
      </TextV2>
    </Flex>
  );
};
